import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faFilter,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import {
    Input,
    Autocomplete,
    AutocompleteItem,
    Select,
    SelectItem,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Button,
} from "@heroui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Key } from "@react-types/shared";
import useAuth from "@/lib/auth/useAuth";
import { EnrollmentTier, TIER_OPTIONS } from "@/lib/utils/enrollment";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";

export interface FieldFilterBarProps {
    searchValue?: string;
    onSearchChange: (value: string) => void;

    serviceProviderId?: string;
    onServiceProviderChange: (id: string | undefined) => void;
    serviceProviderOptions?: { key: string; label: string }[];

    growerId?: string;
    onGrowerChange: (id: string | undefined) => void;
    growerOptions?: { key: string; label: string }[];

    accountManagerId?: string;
    onAccountManagerChange?: (id: string | undefined) => void;
    accountManagerOptions?: { key: string; label: string }[];

    enrollmentTier?: string;
    onEnrollmentTierChange?: (id: string | undefined) => void;
}

export function FieldFilterBar({
    searchValue,
    onSearchChange,
    serviceProviderId,
    onServiceProviderChange,
    serviceProviderOptions = [],
    growerId,
    onGrowerChange,
    growerOptions = [],
    accountManagerId,
    onAccountManagerChange,
    accountManagerOptions = [],
    enrollmentTier,
    onEnrollmentTierChange,
}: FieldFilterBarProps) {
    const { userIsSentinel, isConsultantOrSentinel } = useAuth();
    const [debouncedSearch, setDebouncedSearch] = useState(searchValue ?? "");
    const onSearchChangeRef = useRef(onSearchChange);
    onSearchChangeRef.current = onSearchChange;

    useEffect(() => {
        setDebouncedSearch(searchValue ?? "");
    }, [searchValue]);

    useEffect(() => {
        if (debouncedSearch === (searchValue ?? "")) return;
        const handler = setTimeout(() => {
            onSearchChangeRef.current(debouncedSearch);
        }, 700);

        return () => clearTimeout(handler);
    }, [debouncedSearch, searchValue]);

    const [serviceProviderKey, setServiceProviderKey] = useState<Key | null>(
        serviceProviderId || null,
    );
    const [growerKey, setGrowerKey] = useState<Key | null>(growerId || null);
    const [accountManagerKey, setAccountManagerKey] = useState<Key | null>(
        accountManagerId || null,
    );
    const [enrollmentTierKeys, setEnrollmentTierKeys] = useState(
        new Set(enrollmentTier ? [enrollmentTier] : []),
    );

    useEffect(() => {
        setServiceProviderKey(serviceProviderId || null);
    }, [serviceProviderId]);

    useEffect(() => {
        setGrowerKey(growerId || null);
    }, [growerId]);

    useEffect(() => {
        setAccountManagerKey(accountManagerId || null);
    }, [accountManagerId]);

    useEffect(() => {
        setEnrollmentTierKeys(new Set(enrollmentTier ? [enrollmentTier] : []));
    }, [enrollmentTier]);

    const handleServiceProviderChange = (key: Key | null) => {
        setServiceProviderKey(key);
        onServiceProviderChange(key ? key.toString() : undefined);
    };

    const handleGrowerChange = (key: Key | null) => {
        setGrowerKey(key);
        onGrowerChange(key ? key.toString() : undefined);
    };

    const handleAccountManagerChange = (key: Key | null) => {
        setAccountManagerKey(key);
        if (onAccountManagerChange) {
            onAccountManagerChange(key ? key.toString() : undefined);
        }
    };

    const handleEnrollmentTierChange = (key: Key | null) => {
        if (key) {
            setEnrollmentTierKeys(new Set([key.toString()]));
        } else {
            setEnrollmentTierKeys(new Set([]));
        }
        if (onEnrollmentTierChange) {
            onEnrollmentTierChange(key ? key.toString() : undefined);
        }
    };

    const {
        organizationMonitoringEnabled,
        irrigationEnabled,
        organizationPreviewEnabled,
    } = useFeatureFlag();

    const tierOptions = useMemo(() => {
        let options = TIER_OPTIONS;
        if (!organizationMonitoringEnabled) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Monitoring,
            );
        }
        if (!irrigationEnabled) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Irrigation,
            );
        }
        if (!organizationPreviewEnabled && !userIsSentinel) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Preview,
            );
        }
        return [{ tier: "", label: "All" }, ...options];
    }, [
        organizationMonitoringEnabled,
        irrigationEnabled,
        organizationPreviewEnabled,
        userIsSentinel,
    ]);

    const activeSentinelFilterCount =
        (accountManagerId ? 1 : 0) + (enrollmentTier ? 1 : 0);

    return (
        <div className="flex flex-row gap-2 items-center">
            <Input
                placeholder="Search..."
                size="sm"
                startContent={<FontAwesomeIcon icon={faSearch} />}
                classNames={{
                    base: "min-w-52 w-52",
                }}
                value={debouncedSearch}
                onValueChange={setDebouncedSearch}
                isClearable
            />
            {userIsSentinel && (
                <Autocomplete
                    placeholder="Service Provider"
                    size="sm"
                    startContent={<FontAwesomeIcon icon={faSearch} />}
                    classNames={{
                        base: "min-w-52 w-52",
                    }}
                    aria-label="Service Provider"
                    isVirtualized
                    selectedKey={serviceProviderKey}
                    onSelectionChange={handleServiceProviderChange}
                    allowsCustomValue={false}
                >
                    {serviceProviderOptions.map((option) => (
                        <AutocompleteItem key={option.key}>
                            {option.label}
                        </AutocompleteItem>
                    ))}
                </Autocomplete>
            )}
            {isConsultantOrSentinel && (
                <Autocomplete
                    placeholder="Farm"
                    size="sm"
                    startContent={<FontAwesomeIcon icon={faSearch} />}
                    classNames={{
                        base: "min-w-52 w-52",
                    }}
                    aria-label="Farm"
                    isVirtualized
                    selectedKey={growerKey}
                    onSelectionChange={handleGrowerChange}
                    allowsCustomValue={false}
                >
                    {growerOptions.map((option) => (
                        <AutocompleteItem key={option.key}>
                            {option.label}
                        </AutocompleteItem>
                    ))}
                </Autocomplete>
            )}

            {userIsSentinel &&
                (!!onAccountManagerChange || !!onEnrollmentTierChange) && (
                    <Popover placement="bottom-end">
                        <PopoverTrigger>
                            <Button
                                size="sm"
                                color={
                                    activeSentinelFilterCount > 0
                                        ? "primary"
                                        : "default"
                                }
                                startContent={
                                    <FontAwesomeIcon icon={faFilter} />
                                }
                                endContent={
                                    <FontAwesomeIcon icon={faChevronDown} />
                                }
                                aria-label="More filters"
                            >
                                Filters
                                {activeSentinelFilterCount > 0 &&
                                    ` (${activeSentinelFilterCount})`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-3">
                            <div className="flex flex-col gap-3 min-w-52">
                                {!!onAccountManagerChange && (
                                    <Autocomplete
                                        placeholder="AM"
                                        size="sm"
                                        startContent={
                                            <FontAwesomeIcon icon={faSearch} />
                                        }
                                        aria-label="Account Manager"
                                        isVirtualized
                                        selectedKey={accountManagerKey}
                                        onSelectionChange={
                                            handleAccountManagerChange
                                        }
                                        allowsCustomValue={false}
                                    >
                                        {accountManagerOptions.map((option) => (
                                            <AutocompleteItem key={option.key}>
                                                {option.label}
                                            </AutocompleteItem>
                                        ))}
                                    </Autocomplete>
                                )}
                                {!!onEnrollmentTierChange && (
                                    <Select
                                        placeholder="Tier"
                                        size="sm"
                                        aria-label="Tier"
                                        selectedKeys={enrollmentTierKeys}
                                        selectionMode="single"
                                        onSelectionChange={(keys) => {
                                            handleEnrollmentTierChange(
                                                keys.currentKey ?? null,
                                            );
                                        }}
                                    >
                                        {tierOptions.map((option) => (
                                            <SelectItem key={option.tier}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
        </div>
    );
}
